import { memo, useEffect, useMemo, useRef } from 'react';

import { useEmbedResize } from '@/hooks/use-embed-resize';
import { cn } from '@/utils/cn';

/** Must match client-forms embed message types. */
const EMBED_PREFILL_MESSAGE_TYPE = 'weive-form-prefill';
const EMBED_READY_MESSAGE_TYPE = 'weive-form-embed-ready';

type WeiveFormEmbedProps = {
  publicKey: string;
  formsOrigin: string;
  title: string;
  minHeight?: number;
  className?: string;
  /** Sent to the iframe via postMessage after the form signals it is ready. */
  prefill?: Record<string, string>;
};

function WeiveFormEmbedInner({
  publicKey,
  formsOrigin,
  title,
  minHeight = 760,
  className,
  prefill,
}: WeiveFormEmbedProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const prefillRef = useRef(prefill);
  prefillRef.current = prefill;
  const iframeHeight = useEmbedResize(formsOrigin, { minHeight });
  const formsOriginUrl = useMemo(() => new URL(formsOrigin), [formsOrigin]);

  const src = useMemo(() => {
    const params = new URLSearchParams();
    params.set('embed', '1');
    params.set('parentUrl', window.location.href);
    return `${formsOrigin}/f/${encodeURIComponent(publicKey)}?${params.toString()}`;
  }, [formsOrigin, publicKey]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const contentWindow = iframe.contentWindow;

    const sendPrefill = () => {
      const values = prefillRef.current;
      if (!values || Object.keys(values).length === 0) return;
      iframe.contentWindow?.postMessage(
        { type: EMBED_PREFILL_MESSAGE_TYPE, values },
        formsOriginUrl.origin,
      );
    };

    function onMessage(event: MessageEvent) {
      if (event.origin !== formsOriginUrl.origin) return;
      if (event.source !== contentWindow) return;
      const data = event.data as { type?: string } | null;
      if (data?.type !== EMBED_READY_MESSAGE_TYPE) return;
      sendPrefill();
    }

    window.addEventListener('message', onMessage);
    iframe.addEventListener('load', sendPrefill);

    const retryInterval = window.setInterval(sendPrefill, 500);
    const retryTimeout = window.setTimeout(() => window.clearInterval(retryInterval), 6000);

    return () => {
      window.removeEventListener('message', onMessage);
      iframe.removeEventListener('load', sendPrefill);
      window.clearInterval(retryInterval);
      window.clearTimeout(retryTimeout);
    };
  }, [formsOriginUrl.origin, src]);

  return (
    <div
      className={cn('weive-form-embed', className)}
      style={{ height: iframeHeight }}
    >
      <iframe
        ref={iframeRef}
        src={src}
        title={title}
        loading="lazy"
        style={{ background: 'transparent', colorScheme: 'light' }}
      />
    </div>
  );
}

export const WeiveFormEmbed = memo(WeiveFormEmbedInner);
